import { z } from 'zod';
import { ActedForAction, ActForPerson, ActForRole } from './acting-for-base.js';
import { AwardTypeCode, StageId } from './awards.js';
import { Cents } from './common.js';
import { DocumentView } from './documents.js';
import { PageInfo } from './paging.js';

export * from './acting-for-base.js';

// APP-148: a Stock Admin acting for an Executive or a Sector Lead. The word lists and the fields
// other contracts take (`ActingFor`, `ActForOffer`) are in `acting-for-base.ts`.

const Ref = z.object({ id: z.string(), name: z.string() });
const At = z.iso.datetime({ offset: true });

export const QuestionKind = z.enum(['not_asked', 'different', 'other']);
export type QuestionKind = z.infer<typeof QuestionKind>;
export const SettledOutcome = z.enum(['confirmed', 'returned', 'resolved']);
export type SettledOutcome = z.infer<typeof SettledOutcome>;

/**
 * Where an award stands for a question now (R65): `holds`, a question would hold it;
 * `past_letters`, its letters went out since; `moved_on`, it went on again since (a returned award
 * sent again) or a held one is back in draft; `stopped`, it ended or the action stopped it;
 * `no_award`, a budget, setting or Spot funding change.
 */
export const QuestionStanding = z.enum([
  'holds',
  'past_letters',
  'moved_on',
  'stopped',
  'no_award',
]);
export type QuestionStanding = z.infer<typeof QuestionStanding>;

export const QuestionNow = z.object({
  standing: QuestionStanding,
  /** Who a return sends the award back to (R24: the Sector Lead it was entered for). */
  owner: Ref.nullable(),
  /**
   * The award's plain stage now, as its owner sees it, by id: the page words it ("waiting for
   * approval", "with the Stock Admin team"). Null without an award, or once it ended.
   */
  stage: StageId.nullable(),
  /** What a return takes back out of each pool (D17), from the action's own top-ups. */
  topUps: z.array(z.object({ pool: z.string(), cashCents: Cents, options: z.int().nonnegative() })),
});
export type QuestionNow = z.infer<typeof QuestionNow>;

/** One thing done for someone, as a reader sees it. */
export const ActedForView = z.object({
  id: z.uuid(),
  at: At,
  action: ActedForAction,
  actor: z.object({ id: z.uuid(), name: z.string(), roles: z.array(z.string()) }),
  for: z.object({ id: z.uuid(), name: z.string(), role: ActForRole, roleLabel: z.string() }),
  award: z
    .object({
      id: z.uuid(),
      displayId: z.string().nullable(),
      type: AwardTypeCode,
      title: z.string().nullable(),
    })
    .nullable(),
  /** "Meredith Okonkwo approved SDB-2026-0156 for you": the engine's words, per reader. */
  line: z.string(),
  detail: z.string().nullable(),
  /**
   * What it was, without the note or the files (Done for you's line, the ExecHome board):
   * "HydroVision sea trials · 7 people · $21,000", "with a $2,913 top-up to Terrestrial". Null when
   * the line says it all.
   */
  facts: z.string().nullable(),
  note: z.string().nullable(),
  files: z.array(DocumentView),
  question: z.object({ at: At, by: Ref, kind: QuestionKind, text: z.string() }).nullable(),
  settled: z
    .object({ at: At, by: Ref, outcome: SettledOutcome, note: z.string().nullable() })
    .nullable(),
  held: z.boolean(),
  can: z.object({
    question: z.boolean(),
    settle: z.boolean(),
    edit: z.boolean(),
    /**
     * R98: whether the reader may read what was done (R41). False only on a held row the award's
     * read sends to whoever settles it without being a reader (the sector's current lead), which
     * then carries no question words, note or files. Absent where only readers get rows.
     */
    read: z.boolean().optional(),
  }),
  /**
   * What "Not what you asked?" would do now (D17, the Question board's "What happens next"), for
   * the person it was for while they may still ask; null otherwise.
   */
  asking: QuestionNow.nullable(),
});
export type ActedForView = z.infer<typeof ActedForView>;

/** Where a row's award stands, as its pill says it (R46: "Waiting on Graham Whitlock"). */
export const OthersState = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('executives') }),
  z.object({ kind: z.literal('lead'), lead: Ref }),
  z.object({ kind: z.literal('returned'), lead: Ref }),
]);
export type OthersState = z.infer<typeof OthersState>;

/** Why it waits, the meta line's last words before how long (R46). */
export const OthersWhy = z.discriminatedUnion('kind', [
  /** Over budget by this much cash when it was sent (R37); null for a send from before. */
  z.object({ kind: z.literal('over'), shortfall: Cents.nullable() }),
  z.object({ kind: z.literal('executive_decision') }),
  z.object({ kind: z.literal('delegate_entry') }),
  /** Returned to its lead: by whom, with what reason, when the trail has them. */
  z.object({ kind: z.literal('returned'), by: Ref.nullable(), note: z.string().nullable() }),
  z.object({ kind: z.literal('spot_funding') }),
]);
export type OthersWhy = z.infer<typeof OthersWhy>;

/**
 * The decision a row waits on, finer than its section (R68): an Executive award sent before
 * self-recognition changed (Main board row 4) shares the PAA and TAA section, but the Executive's
 * kind switch shows it on its own tab.
 */
export const OthersDecision = z.enum([
  'over_budget',
  'paa_taa',
  'executive_award',
  'spot_funding',
  /** APP-274: a delegate's entry waiting on the Executive it was entered for. */
  'executive_delegate_entry',
  'lead_delegate_entry',
  'lead_returned',
]);
export type OthersDecision = z.infer<typeof OthersDecision>;

/**
 * A row of "Waiting on others" on a Stock Admin's Home and Approvals. The structured fields draw
 * the Queue board's row like for like (R46); `title`, `detail` and `status` are the Home board's
 * words for the same row.
 */
export const OthersRow = z.object({
  key: z.string(),
  href: z.string(),
  since: At,
  section: z.enum([
    'over_budget',
    'paa_taa',
    'spot_funding',
    'executive_delegates',
    'sector_leads',
  ]),
  decision: OthersDecision,
  /** The award's ID ("SDB-2026-0156"); for Spot funding, "Funding for ‹nominee›". */
  ref: z.string(),
  state: OthersState,
  /** The pools over budget, each with its cash shortfall when it was sent (R37), else null. */
  overPools: z.array(z.object({ pool: z.string(), shortfall: Cents.nullable() })),
  /** The type line: "Sector Discretionary Bonus · 8 people · $42,000". */
  typeName: z.string(),
  people: z.int().nonnegative(),
  amount: Cents,
  /** Options given, or null for none. */
  options: z.int().positive().nullable(),
  /** Who entered it (for Spot funding, who asked for the funding), and for whom when it's a delegate's. */
  enteredBy: Ref.nullable(),
  enteredFor: Ref.nullable(),
  /** The award's sector; for Spot funding, the pool the funding draws on. */
  sector: z.string().nullable(),
  awardTitle: z.string().nullable(),
  why: OthersWhy,
  title: z.string(),
  detail: z.string(),
  /**
   * A sentence after the detail and how long it has waited (R38, the Home board): "The top-up
   * defaults to $2,913." Null when there's none.
   */
  detailEnd: z.string().nullable(),
  status: z.string(),
  whose: z.object({ role: ActForRole, person: Ref.nullable() }),
});
export type OthersRow = z.infer<typeof OthersRow>;

export const WaitingOnOthers = z.object({
  rows: z.array(OthersRow),
  counts: z.object({ executives: z.int(), sectorLeads: z.int() }),
  switches: z.object({ executives: z.boolean(), sectorLeads: z.boolean() }),
  lastDone: ActedForView.nullable(),
});
export type WaitingOnOthers = z.infer<typeof WaitingOnOthers>;

export const ActedForList = z.object({ items: z.array(ActedForView), page: PageInfo });
export type ActedForList = z.infer<typeof ActedForList>;

/** "Not what you asked?": the Executive or Sector Lead says what is off. */
export const QuestionActedFor = z
  .object({ kind: QuestionKind, text: z.string().trim().min(1).max(2000) })
  .strict();
export type QuestionActedFor = z.infer<typeof QuestionActedFor>;

/**
 * Settling "Not what you asked?": confirm it as it is, or return the award to whoever entered it.
 * A return says why it goes back (R36): the settler's own reason, which the award's draft keeps
 * and its creator reads, as an Executive's return of an award does.
 */
export const SettleActedFor = z
  .object({ outcome: z.enum(['confirm', 'return']), note: z.string().trim().max(2000).optional() })
  .strict()
  .refine((v) => v.outcome !== 'return' || (v.note !== undefined && v.note !== ''), {
    message: 'Say why it’s going back.',
    path: ['note'],
  });
export type SettleActedFor = z.infer<typeof SettleActedFor>;

export const EditActedForNote = z.object({ note: z.string().trim().max(2000) }).strict();
export type EditActedForNote = z.infer<typeof EditActedForNote>;

/** A file uploaded while the step is open; attached when the action confirms (D14). */
export const StagedFile = z.object({ document: DocumentView });
export type StagedFile = z.infer<typeof StagedFile>;

/** Who a Stock Admin may name, with the two switches' state. */
export const ActForPeople = z.object({
  executives: z.array(ActForPerson),
  switches: z.object({ executives: z.boolean(), sectorLeads: z.boolean() }),
  sectorLeads: z.array(z.object({ sector: Ref, lead: ActForPerson.nullable() })),
});
export type ActForPeople = z.infer<typeof ActForPeople>;
